import type { EngagementRate } from '@clockwise/clients/sows';
import { Trash2 } from 'lucide-react';
import { CurrencyInput } from '@/components/custom/currency-input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { rateError } from '@/lib/rate-validation';

/**
 * An editable list of per-role rates.
 *
 * Used twice, deliberately: a T&M engagement's own `rates`, and a retainer's
 * `overageRates` — what hours ABOVE its budget bill at. They are the same
 * thing measured at different times, so they get the same control rather than
 * two that drift apart.
 *
 * The list is seeded from the SOW's selected rate card when the engagement is
 * created. Here the operator only overrides rates or drops roles they don't
 * need, so the engagement always mirrors the card it was built from.
 */
export function EngagementRateTable({
  label,
  rates,
  roleLookup,
  disabled,
  emptyHint,
  onChange,
}: {
  label: string;
  rates: EngagementRate[];
  roleLookup: (id: string) => string;
  disabled?: boolean;
  emptyHint: string;
  onChange: (next: EngagementRate[]) => void;
}) {
  const updateRate = (i: number, rate: number) => {
    onChange(rates.map((r, idx) => (idx === i ? { ...r, rate } : r)));
  };
  const removeRate = (i: number) => {
    onChange(rates.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-2">
      <Label>
        {label} ({rates.length})
      </Label>
      {rates.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {emptyHint}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {rates.map((rate, i) => {
            const name = roleLookup(rate.roleId);
            const err = rateError(rate.rate);
            return (
              <li
                key={rate.roleId}
                className="grid grid-cols-[1fr_140px_auto] items-start gap-3 px-3 py-2"
              >
                <span className="pt-2 text-sm">{name}</span>
                <div className="space-y-1">
                  <CurrencyInput
                    value={rate.rate}
                    onChange={(next) => updateRate(i, next)}
                    disabled={disabled}
                    aria-label={`Rate for ${name}`}
                    className={
                      err ? 'border-destructive focus-visible:ring-destructive' : undefined
                    }
                  />
                  {err ? (
                    <p className="text-xs text-destructive" role="alert">
                      {err}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  className="mt-0.5"
                  onClick={() => removeRate(i)}
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** True when any rate departs from the rate-card rate snapshotted when the
 *  role was added. Compared against the engagement's OWN stored values, so a
 *  later global rate-card edit can never flip an engagement's status. */
export function isRateSetCustomized(rates: readonly EngagementRate[]): boolean {
  return rates.some((r) => r.rate !== r.baseRate);
}

/**
 * Display label for a service role: "CODE · Name" when a short code is known,
 * otherwise just the name. Shared by the page's role lookup and the picker so
 * roles read consistently across the engagement card and the edit sheet.
 */
export function formatRoleLabel(shortCode: string | null | undefined, name: string): string {
  return shortCode ? `${shortCode} · ${name}` : name;
}
